import React, {useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, Grid, IconButton, Paper, Typography, useTheme} from "@mui/material";
import {DeleteRounded, UploadRounded, VisibilityRounded} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import {useForm, FormProvider} from "react-hook-form";
import {useMutation} from "@tanstack/react-query";
import {yupResolver} from "@hookform/resolvers/yup";
import dayjs, {Dayjs} from "dayjs";
import _ from "underscore";
import {v4} from "uuid";
import {FormAutocomplete, FormCheckbox, FormDateTimePicker, FormTextField} from "../../common/form-fields-wrappers";
import {BATTERY_LEVEL_OPTIONS, TANK_LEVEL_OPTIONS} from "../../data";
import CheckStepper from "../check-stepper/CheckStepper";
import Preview from "../../preview/Preview";
import {checkOutInModeState, checkOutInStepState, checkVehicleStatusState, substitutionCheckInState, raFetchedDataState} from "../state";
import {IGenericOption, IVehicleStatusForm} from "../../models/models";
import {ELECTRIC_ENGINE_POWER_CODE, TERMIC_HYBRID_ENGINE_POWER_CODE} from "../../config";
import {THEFT_REASON_CODE, VEHICLE_STATUS_FILE_UPLOAD_NAME, VEHICLE_STATUS_FORM_VALIDATION_SCHEMA, VEHICLE_STATUS_INITIAL_VALUE} from "../config";
import {fetchCheckKeyBox, fetchCheckOutInVehicleStatus} from "../services/http.services";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";
import {NetworkScope} from "../../types";
import {hasScope, resizeImg} from "../../utils";
import {deepClone} from "../../../../util/state/commonFunctions";
import {recoveryRaModeState} from "../../create-ra/state";

type VehicleStatusProps = {
	hasUnconfirmedCustomerReportsValue?: boolean; // Opzionale
  };

const VehicleStatus = (props: VehicleStatusProps) => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const { globalData, dispatchGlobalData } = useContext(GlobalContext);

	const checkOutInMode = useRecoilValue(checkOutInModeState);
	const recoveryMode = useRecoilValue(recoveryRaModeState);
	const [checkStep, setCheckStep] = useRecoilState(checkOutInStepState);
	// fetch from BE
	const raFetchedData = useRecoilValue(raFetchedDataState);
	// non runnning vehicle
	const substitutionCheckIn = useRecoilValue(substitutionCheckInState);
	// inserito	=> kms, tank, battery & photos
	const [checkVehicle, setCheckVehicle] = useRecoilState(checkVehicleStatusState);
	// manage preview
	const [openPreviewAttach, setOpenPreviewAttach] = useState(false);
	const [filePreview, setFilePreview] = useState(null);
	// effective date-time for check-in
	const [effectiveDateTime, setEffectiveDateTime] = useState<Dayjs>();
	const [minEffectiveDropOffDate, setMinEffectiveDropOffDate] = useState<Dayjs>();
	const [maxEffectiveDropOffDate, setMaxEffectiveDropOffDate] = useState<Dayjs>();
	// Authorities
	const [raRetroDatableCheckIn, setRaRetroDatableCheckIn] = useState<boolean>(false);
	const [keyBoxServiceApplicable, setKeyBoxServiceApplicable] = useState<boolean>(false);
	// DropOff Location list
	const [dropOffLocationList, setDropOffLocationList] = useState<IGenericOption[]>([]);

	const rardciAuth = useMemo(() => hasScope(globalData, NetworkScope.RaRetroDatableCheckIn), [globalData]);
	const rakbciAuth = useMemo(() => hasScope(globalData, NetworkScope.RaKeyBoxCheckIn), [globalData]);

	// FIXME: mock, get data from BE
	const tankLevelOptions: any = TANK_LEVEL_OPTIONS;
	const batteryLevelOptions: any = BATTERY_LEVEL_OPTIONS;

	const showEngineRunning = useMemo(() => {
		const tenants: string[] =
			globalData.user.legalEntities
				.filter((elem: any) => elem.name !== 'all')
				.map((elem: any) => elem.name);

		return !!raFetchedData.idRentalAgreement && (tenants.includes('drivalia_fr') || tenants.includes('drivalia_francia'));
	}, [globalData, raFetchedData]);

	const vehicleStatusForm = useForm<IVehicleStatusForm>({
		defaultValues: getVehicleStatusDefaultValues(raFetchedData),
		resolver: yupResolver(
			VEHICLE_STATUS_FORM_VALIDATION_SCHEMA
		)
	});

	let {kmsOnCheckPhoto, tankLevelOnCheckPhoto, batteryLevelOnCheckPhoto,
		nonRunningVehicle, subRreasonCode} = vehicleStatusForm.getValues();
	const errors = vehicleStatusForm.formState.errors;

	const enginePowerCode = vehicleStatusForm.getValues('enginePowerGroupCode');

	const hasOneWayAncillary = useMemo(() => {
		if (!!raFetchedData) {
			return raFetchedData.bookingDetail?.ancillaries?.filter((ancillary: any) => ancillary.ancillaryCode === 'OW' )?.length > 0;
		}
		return false;
	}, [raFetchedData]);

	function getVehicleStatusDefaultValues(raFetchedData: any): IVehicleStatusForm {
		const defaultVehicleStatusValue = VEHICLE_STATUS_INITIAL_VALUE;

		if (['in', 'subIn'].includes(checkOutInMode)) {
			defaultVehicleStatusValue.kms = 					!!raFetchedData.checkOut.kmsOnCheck ? raFetchedData.checkOut.kmsOnCheck : (!!raFetchedData.vehicle?.kilometersOnStart ? raFetchedData.vehicle.kilometersOnStart : '');
			defaultVehicleStatusValue.currentTankFilling = 		!!raFetchedData.checkOut.tankLevelOnCheck ? raFetchedData.checkOut.tankLevelOnCheck + '/8' : null;
			defaultVehicleStatusValue.currentBatteryFilling = 	!!raFetchedData.checkOut.batteryLevelOnCheck ? raFetchedData.checkOut.batteryLevelOnCheck + '%' : null;
		} else { // case: out | outOpr | inOpr
			defaultVehicleStatusValue.kms = 					!!raFetchedData.vehicle.kilometersOnStart ? raFetchedData.vehicle.kilometersOnStart : '';
			defaultVehicleStatusValue.currentTankFilling = 		!!raFetchedData.vehicle.currentTermicTankFilling ? raFetchedData.vehicle.currentTermicTankFilling + '/8' : null;
			defaultVehicleStatusValue.currentBatteryFilling = 	!!raFetchedData.vehicle.currentElectricTankFilling ? raFetchedData.vehicle.currentElectricTankFilling + '%' : null;
		}

		defaultVehicleStatusValue.enginePowerGroupCode = 	raFetchedData.vehicle.enginePowerGroupCode;
		defaultVehicleStatusValue.checkOutInMode = 			checkOutInMode;

		if (!_.isEmpty(checkVehicle)) {
			defaultVehicleStatusValue.now = 				checkVehicle.now;
			defaultVehicleStatusValue.kmsOnCheck = 			checkVehicle.kmsOnCheck;
			defaultVehicleStatusValue.tankLevelOnCheck = 	checkVehicle.tankLevelOnCheck;
			defaultVehicleStatusValue.batteryLevelOnCheck = checkVehicle.batteryLevelOnCheck;

			// Photos
			defaultVehicleStatusValue.kmsOnCheckPhoto = 		checkVehicle.kmsOnCheckPhoto;
			defaultVehicleStatusValue.tankLevelOnCheckPhoto = 	checkVehicle.tankLevelOnCheckPhoto;
			defaultVehicleStatusValue.batteryLevelOnCheckPhoto = checkVehicle.batteryLevelOnCheckPhoto;

			// effective date for CheckIn
			defaultVehicleStatusValue.effectiveDropOffDate = 	checkVehicle.effectiveDateTime;
			defaultVehicleStatusValue.minEffectiveDropOffDate = checkVehicle.minEffectiveDropOffDate;
			defaultVehicleStatusValue.maxEffectiveDropOffDate = checkVehicle.maxEffectiveDropOffDate;
			defaultVehicleStatusValue.effectiveDateTime = 		checkVehicle.effectiveDateTime;

			defaultVehicleStatusValue.dropOffLocation = 		checkVehicle.dropOffLocation;
			defaultVehicleStatusValue.nonRunningVehicle = 		checkVehicle.nonRunningVehicle;

			defaultVehicleStatusValue.recoveryMode = 			checkVehicle.recoveryMode;
		}
		else { // raFetchedData
			if (['in', 'subIn'].includes(checkOutInMode)) {
				const ms =
					globalData.user.mobilityStores
						.map((elem: any) => {
							return {
								code: elem.bpCode,
								description: elem.name,
								id: null,
								label: elem.name,
							}
						});

				const msCode = ms.map((elem: any) => elem.code);
				if (msCode.includes(raFetchedData.bookingDetail?.dropOffLocation?.code)) {
					defaultVehicleStatusValue.dropOffLocation = raFetchedData.bookingDetail?.dropOffLocation;
				} else {
					defaultVehicleStatusValue.dropOffLocation = null;
				}
			}

			defaultVehicleStatusValue.now = 					dayjs();
			defaultVehicleStatusValue.effectiveDropOffDate = 	(!!substitutionCheckIn?.nonRunningVehicle && !!substitutionCheckIn?.eventDate) ? dayjs(substitutionCheckIn?.eventDate) : dayjs();
			defaultVehicleStatusValue.minEffectiveDropOffDate = minEffectiveDropOffDate;
			defaultVehicleStatusValue.maxEffectiveDropOffDate = maxEffectiveDropOffDate;
			defaultVehicleStatusValue.effectiveDateTime = 		effectiveDateTime;
			defaultVehicleStatusValue.nonRunningVehicle = 		!!substitutionCheckIn?.nonRunningVehicle || substitutionCheckIn?.reasonCode === THEFT_REASON_CODE;
			defaultVehicleStatusValue.subRreasonCode = 			substitutionCheckIn?.reasonCode === THEFT_REASON_CODE;

			defaultVehicleStatusValue.recoveryMode =			recoveryMode;
		}
		return defaultVehicleStatusValue;
	}

	function handelNonRunningVehicle(e: React.SyntheticEvent | null, value: boolean): void {
		if (value) {// non running
			const vehicleStatus = vehicleStatusForm.getValues();

			vehicleStatusForm.setValue('kmsOnCheck', vehicleStatus.kms);
			if (ELECTRIC_ENGINE_POWER_CODE.includes(enginePowerCode as string) && !vehicleStatus.currentBatteryFilling) {
				vehicleStatusForm.setValue('batteryLevelOnCheck', batteryLevelOptions[10] as any);
			} else {
				const currentBatteryFilling = batteryLevelOptions.filter((elem: any) => elem.description === vehicleStatus.currentBatteryFilling);
				vehicleStatusForm.setValue('batteryLevelOnCheck', currentBatteryFilling[0] ?? null);
			}

			if (TERMIC_HYBRID_ENGINE_POWER_CODE.includes(enginePowerCode as string) && !vehicleStatus.currentTankFilling) {
				vehicleStatusForm.setValue('tankLevelOnCheck', tankLevelOptions[8]);
			} else {
				const currentTankFilling = tankLevelOptions.filter((elem: any) => elem.description === vehicleStatus.currentTankFilling);
				vehicleStatusForm.setValue('tankLevelOnCheck', currentTankFilling[0] ?? null);
			}

			setCheckVehicle({
				kmsOnCheck: 				vehicleStatus.kmsOnCheck,
				kmsOnCheckPhoto: 			vehicleStatus.kmsOnCheckPhoto,
				tankLevelOnCheck: 			vehicleStatus.tankLevelOnCheck,
				tankLevelOnCheckPhoto: 		vehicleStatus.tankLevelOnCheckPhoto,
				batteryLevelOnCheck: 		vehicleStatus.batteryLevelOnCheck,
				batteryLevelOnCheckPhoto: 	vehicleStatus.batteryLevelOnCheckPhoto,
				now:						vehicleStatus.now,
				effectiveDateTime:			effectiveDateTime,
				minEffectiveDropOffDate:	minEffectiveDropOffDate,
				dropOffLocation:			vehicleStatus.dropOffLocation,
				nonRunningVehicle: 			value,
				subRreasonCode: 			vehicleStatus.subRreasonCode,
				raRetroDatableCheckIn: 		vehicleStatus.raRetroDatableCheckIn,
				keyBoxServiceApplicable: 	vehicleStatus.keyBoxServiceApplicable,
				recoveryMode: 				vehicleStatus.recoveryMode
			});
		} else {
			vehicleStatusForm.setValue('kmsOnCheck', 			checkVehicle.kmsOnCheck);
			vehicleStatusForm.setValue('batteryLevelOnCheck', checkVehicle.batteryLevelOnCheck);
			vehicleStatusForm.setValue('tankLevelOnCheck', 	checkVehicle.tankLevelOnCheck);

			const checkVhcl = deepClone(checkVehicle);
			checkVhcl.nonRunningVehicle = value;
			setCheckVehicle(checkVhcl);
		}
	}

	/** Update Vehicle Status */
	const updateVehicleStatusMutation = useMutation(
		fetchCheckOutInVehicleStatus,
		{
			onSuccess: (response: any) => {
				const vehicleStatus = vehicleStatusForm.getValues();
				setCheckVehicle({
					kmsOnCheck: 				vehicleStatus.kmsOnCheck,
					kmsOnCheckPhoto: 			vehicleStatus.kmsOnCheckPhoto,
					tankLevelOnCheck: 			vehicleStatus.tankLevelOnCheck,
					tankLevelOnCheckPhoto: 		vehicleStatus.tankLevelOnCheckPhoto,
					batteryLevelOnCheck: 		vehicleStatus.batteryLevelOnCheck,
					batteryLevelOnCheckPhoto: 	vehicleStatus.batteryLevelOnCheckPhoto,
					effectiveDateTime:			vehicleStatus.effectiveDropOffDate,
					minEffectiveDropOffDate:	vehicleStatus.minEffectiveDropOffDate,
					dropOffLocation:			vehicleStatus.dropOffLocation,
					nonRunningVehicle: 			checkVehicle?.nonRunningVehicle,
					subRreasonCode: 			vehicleStatus?.subRreasonCode,
					now: 						vehicleStatus.now,
					raRetroDatableCheckIn: 		vehicleStatus.raRetroDatableCheckIn,
					keyBoxServiceApplicable: 	vehicleStatus.keyBoxServiceApplicable,
					recoveryMode: 				vehicleStatus.recoveryMode
				});

				setCheckStep({
					active: checkStep.active + 1,
					numSteps: checkStep.numSteps
				});
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	const fetchCheckKeyBoxMutation = useMutation(
		fetchCheckKeyBox,
		{
			onSuccess: (response: {keyBoxServiceApplicable: boolean}) => {
				vehicleStatusForm.setValue('keyBoxServiceApplicable', response.keyBoxServiceApplicable);
				setKeyBoxServiceApplicable(response.keyBoxServiceApplicable);
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	/**
	 * Advances to the next step after data validation
	 */
	const handleContinue = async (vehicleStatus: IVehicleStatusForm) => {
		await updateVehicleStatusMutation.mutateAsync({
				tenant:  raFetchedData.idTenant,
				plate: raFetchedData.vehicle.plate.description,
				payload: {
					currentTermicTankFilling: 	vehicleStatus.tankLevelOnCheck?.code,
					currentElectricTankFilling: vehicleStatus.batteryLevelOnCheck?.code,
					kms: 						parseInt(vehicleStatus.kmsOnCheck)
				}
		} as any);
	}

	const handleDeleteAttachFile = (e: React.SyntheticEvent, field: "kmsOnCheckPhoto" | "tankLevelOnCheckPhoto" | "batteryLevelOnCheckPhoto") => {
		e.stopPropagation();

		vehicleStatusForm.setValue(field, null);
		setFilePreview(null);
		vehicleStatusForm.trigger();
	}

	const handleViewAttachFile = (e: React.SyntheticEvent, field: string) => {
		e.stopPropagation();

		// @ts-ignore
		setFilePreview({damageId: v4(), file: vehicleStatusForm.getValues(field).documentFile});
		setOpenPreviewAttach(true);
	}

	const handleClosePreview = () => {
		setOpenPreviewAttach(false);
	}

	const handleFileOnChange = async (e: React.BaseSyntheticEvent, field: 'kmsOnCheckPhoto' | 'tankLevelOnCheckPhoto' | 'batteryLevelOnCheckPhoto') => {
		e.stopPropagation();

		if (!!e.target.files[0]?.size) {
			const contentType = e.target.files[0].name.split('.');
			let path = '';
			if (checkOutInMode === 'out') {
				path = 'Out';
			} else if (['in', 'subIn'].includes(checkOutInMode)) {
				path = 'In';
			}

			let title = '';
			if (field === 'kmsOnCheckPhoto') {
				title = VEHICLE_STATUS_FILE_UPLOAD_NAME.km;
			} else if (field === 'tankLevelOnCheckPhoto') {
				title = VEHICLE_STATUS_FILE_UPLOAD_NAME.termic;
			} else if (field === 'batteryLevelOnCheckPhoto') {
				title = VEHICLE_STATUS_FILE_UPLOAD_NAME.soc
			}
			title += '.' + contentType[1];

            const resizedFile = await resizeImg(e.target.files[0]);
			let reader = new FileReader();
			// @ts-ignore
			reader.readAsDataURL(resizedFile);
			reader.onload = function() {
				vehicleStatusForm.setValue(field, {
					documentFile: reader.result as string,
					path: raFetchedData.idRentalAgreement + '/check' + path,
					contentType: contentType[1],
					documentTitle: title,
					fileSize: e.target.files[0].size
				});
				vehicleStatusForm.trigger(field);
			};
		}
	}

	const executeCheckRaKeyBoxCheckIn = async (date: Dayjs | string) => {
		if (!rardciAuth && rakbciAuth) {
			await fetchCheckKeyBoxMutation.mutateAsync({
				msSapId: raFetchedData.bookingDetail?.dropOffLocation?.code,
				requestedDate: typeof date === 'string' ? date : date.toISOString()
			} as any);
		}
	}

	/** useEffect's hooks */
	useEffect((): void => {
		let effectiveDT = !_.isEmpty(checkVehicle) ? checkVehicle.effectiveDateTime : dayjs();
		setEffectiveDateTime(effectiveDT);
		vehicleStatusForm.setValue('effectiveDateTime', effectiveDT);

		const maxEffectiveDateTime = dayjs();
		setMaxEffectiveDropOffDate(maxEffectiveDateTime);
		vehicleStatusForm.setValue('maxEffectiveDropOffDate', maxEffectiveDateTime);

		const minEffectiveDateTime = dayjs(raFetchedData.raScanGenerationDate);
		setMinEffectiveDropOffDate(minEffectiveDateTime);
		vehicleStatusForm.setValue('minEffectiveDropOffDate', minEffectiveDateTime);

		// check Auth
		if (['in', 'subIn'].includes(checkOutInMode)) {
			if (!!substitutionCheckIn?.nonRunningVehicle) {
				handelNonRunningVehicle(null, substitutionCheckIn?.nonRunningVehicle);
			}

			setRaRetroDatableCheckIn(rardciAuth);
			if (!!rardciAuth) {
				vehicleStatusForm.setValue('raRetroDatableCheckIn', rardciAuth);
			}

			executeCheckRaKeyBoxCheckIn(raFetchedData.checkIn.expectedCheckInDate);

			const ms =
				globalData.user.mobilityStores
					.map((elem: any) => {
						return {
							code: elem.bpCode,
							description: elem.name,
							id: null,
							label: elem.name,
						}
					})
					.filter((elem: any) => !!elem.code);

			setDropOffLocationList(ms);
		}
	},[]);

	return (
		<Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
			<FormProvider {...vehicleStatusForm}>
				<Grid container columnSpacing={2}>
					{/* Header */}
					<Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
						<Typography variant={"h6"}>
							{t("raCheckOutIn.vehicleStatus.header.title")}
						</Typography>
					</Grid>
					<Grid item xs={12} sx={{display: "flex", justifyContent: "center", mb: theme.spacing(2)}}>
						<Typography variant={"bodyLarge"}>
							{t("raCheckOutIn.vehicleStatus.label.kmAndFuel")}
						</Typography>
					</Grid>

					{/* Content */}
					<Grid container item xs={12} columnSpacing={2} sx={{mb: theme.spacing(1)}}>

						<Grid item xs={12} md={6} sx={{mb: 'auto', display: "flex", justifyContent: "center"}}>
							<img src={"/images/network/Panda.png"} />
						</Grid>

						<Grid container item xs={12} md={6} spacing={2} sx={{mt: theme.spacing(2), mb: 'auto'}}>

							{/* CheckOutIn */}
							{
								['in', 'subIn', 'out'].includes(checkOutInMode) &&
								<Grid item xs={6}>
									<Typography variant={"body"} component={"div"}>
										{t("raCheckOutIn.vehicleStatus.label.raOwner")}
									</Typography>
									<Typography variant={"body"} component={"div"}>
										<strong>
											{raFetchedData.raOwner.firstName} {raFetchedData.raOwner.lastName}
										</strong>
									</Typography>
								</Grid>
							}

							{/* CheckOut */}
							{
								checkOutInMode === "out" &&
								<Grid item xs={6} />
							}

							{/* CheckOutIn */}
							{
								['in', 'subIn', 'out'].includes(checkOutInMode) &&
								<Grid item xs={6}>
									<Typography variant={"body"} component={"div"}>
										{t("raCheckOutIn.vehicleStatus.label.raNumber")}
									</Typography>
									<Typography variant={"body"} component={"div"}>
										<strong>
											{raFetchedData.raNumber}
										</strong>
									</Typography>
								</Grid>
							}

							{/* CheckIn */}
							{
								['in', 'subIn'].includes(checkOutInMode) &&
								<Grid item xs={6}>
									<Typography variant={"body"} component={"div"}>
										{t("raCheckOutIn.vehicleStatus.label.dmsIdCode")}
									</Typography>
									<Typography variant={"body"} component={"div"}>
										<strong>
											{raFetchedData.checkIn?.dmsIdentificationCode?.code}
										</strong>
									</Typography>
								</Grid>
							}

							{/* CheckIn */}
							{
								['in', 'subIn'].includes(checkOutInMode) && !raFetchedData.renewalsSubstitution &&
								<Grid item xs={6} />
							}

							{
								['in', 'subIn'].includes(checkOutInMode) && !!raFetchedData.renewalsSubstitution &&
								<Grid item xs={6} >
									<Typography variant={"body"} component={"div"}>
										{t("raCheckOutIn.vehicleStatus.label.renewalsSubstitution")}
									</Typography>
									<Typography variant={"body"} component={"div"}>
										<strong>
											{raFetchedData.renewalsSubstitution}
										</strong>
									</Typography>
								</Grid>
							}

							{/* CheckOut */}
							{
								checkOutInMode === "out" &&
								<Grid item xs={6}>
									<Typography variant={"body"} component={"div"}>
										{t("raCheckOutIn.vehicleStatus.label.pickUpLocation")}
									</Typography>
									<Typography variant={"body"} component={"div"}>
										<strong>
											{/* effective pickup location */}
											{raFetchedData.bookingDetail?.pickUpLocation?.label}
										</strong>
									</Typography>
								</Grid>
							}

							<Grid item xs={6}>
								<Typography variant={"body"} component={"div"}>
									{t("raCheckOutIn.vehicleStatus.label.vehicleModel")}
								</Typography>
								<Typography variant={"body"} component={"div"}>
									<strong>
										{(!!raFetchedData.vehicle?.brandDescription ? raFetchedData.vehicle.brandDescription + ' ' : '') + (!!raFetchedData.vehicle?.modelDescription ? raFetchedData.vehicle.modelDescription : '')}
									</strong>
								</Typography>
							</Grid>

							<Grid item xs={6}>
								<Typography variant={"body"} component={"div"}>
									{t("raCheckOutIn.vehicleStatus.label.vehiclePlate")}
								</Typography>
								<Typography variant={"body"} component={"div"}>
									<strong>
										{raFetchedData.vehicle.plate.description}
									</strong>
								</Typography>
							</Grid>

							{/* CheckIn*/}
							{
								['in', 'subIn'].includes(checkOutInMode) &&
								<>
									<Grid item xs={6} sx={{display: "flex", alignItems: 'center'}}>
										<Box>
                                            <Typography variant={"body"} component={"div"}>
                                                {t("raCheckOutIn.vehicleStatus.label.pickUpLocation")}
                                            </Typography>
                                            <Typography variant={"body"} component={"div"}>
                                                <strong>
                                                    {/* effective pickup location */}
                                                    {raFetchedData.bookingDetail?.pickUpLocation?.label}
                                                </strong>
                                            </Typography>
										</Box>
									</Grid>

									<Grid item xs={6}>
										{/*<Typography variant={"body"} component={"div"}>*/}
										{/*	{t("raCheckOutIn.vehicleStatus.label.dropOffLocation")}*/}
										{/*</Typography>*/}
										{/*<Typography variant={"body"} component={"div"}>*/}
										{/*	<strong>*/}
										{/*		{raFetchedData.bookingDetail.dropOffLocation?.label}*/}
										{/*	</strong>*/}
										{/*</Typography>*/}

										<FormAutocomplete<IVehicleStatusForm>
											name="dropOffLocation"
											control={vehicleStatusForm.control}
											label={t("raCheckOutIn.vehicleStatus.label.dropOffLocation")}
											fullWidth
											options={dropOffLocationList}
											isOptionEqualToValue={(option, value) => option?.code === value?.code}
											getOptionLabel={(opt) => opt?.description}
											sx={{mr: theme.spacing(1)}}
										/>
									</Grid>
								</>
							}

							{/* CheckOutIn */}
							{
								['in', 'subIn', 'out'].includes(checkOutInMode) &&
									<Grid item xs={6}>
										<Typography variant={"body"} component={"div"}>
											{t("raCheckOutIn.vehicleStatus.label.pickUpDate")}
										</Typography>
										<Typography variant={"body"} component={"div"}>
											<strong>
												{
													!!raFetchedData.checkOut.expectedCheckoutDate ?
														dayjs(raFetchedData.checkOut.expectedCheckoutDate).format('DD/MM/YYYY HH:mm') :
														"-"
												}
											</strong>
										</Typography>
									</Grid>
							}

							{/* CheckOut*/}
							{
								checkOutInMode === "out" &&
									<Grid item xs={6}>
										<Typography variant={"body"} component={"div"}>
											{t("raCheckOutIn.vehicleStatus.label.effectivePickUpDate")}
										</Typography>
										<Typography variant={"body"} component={"div"}>
											<strong>
												{recoveryMode ? dayjs(raFetchedData.recoveryDate).format('DD/MM/YYYY HH:mm') : dayjs().format('DD/MM/YYYY HH:mm')}
											</strong>
										</Typography>
									</Grid>
							}

							{/* CheckIn*/}
							{
								['in', 'subIn'].includes(checkOutInMode) &&
								<Grid item xs={6}>
									<Typography variant={"body"} component={"div"}>
										{t("raCheckOutIn.vehicleStatus.label.effectivePickUpDate")}
									</Typography>
									<Typography variant={"body"} component={"div"}>
										<strong>
											{
												!!raFetchedData.raScanGenerationDate ? dayjs(raFetchedData.raScanGenerationDate).format('DD/MM/YYYY HH:mm') : ''
											}
										</strong>
									</Typography>
								</Grid>
							}

							{/*	CheckIn */}
							{
								['in', 'subIn'].includes(checkOutInMode) &&
								<>
									<Grid item xs={6}>
										<Typography variant={"body"} component={"div"}>
											{t("raCheckOutIn.vehicleStatus.label.dropOffDate")}
										</Typography>
										<Typography variant={"body"} component={"div"}>
											<strong>
												{
													!!raFetchedData.checkIn.expectedCheckInDate ?
														dayjs(raFetchedData.checkIn.expectedCheckInDate).format('DD/MM/YYYY HH:mm') :
														""
												}
											</strong>
										</Typography>
									</Grid>

									<Grid item xs={6}>
										{
											!(raRetroDatableCheckIn || keyBoxServiceApplicable) &&
											<>
												<Typography variant={"body"} component={"div"}>
													{t("raCheckOutIn.vehicleStatus.label.effectiveDropOffDate")}
												</Typography>

												<Typography variant={"body"} component={"div"}>
													<strong>
														{effectiveDateTime?.format('DD/MM/YYYY HH:mm')}
													</strong>
												</Typography>
											</>

										}

										{
											(raRetroDatableCheckIn || keyBoxServiceApplicable || recoveryMode) &&
											<>
												<FormDateTimePicker<IVehicleStatusForm>
													name='effectiveDropOffDate'
													control={vehicleStatusForm.control}
													label={t("raCheckOutIn.vehicleStatus.label.effectiveDropOffDate")}
													minDateTime={minEffectiveDropOffDate}
													maxDateTime={maxEffectiveDropOffDate}
												/>
											</>
										}
									</Grid>

									<Grid item xs={6}>
										<FormCheckbox<IVehicleStatusForm>
											name={`nonRunningVehicle`}
											label={t('raCheckOutIn.vehicleStatus.label.nonRunningVehicle')}
											color={'primary'}
											control={vehicleStatusForm.control}
											onChange={(e: React.SyntheticEvent, value: boolean) => handelNonRunningVehicle(e, value)}
											disabled={subRreasonCode}
										/>
									</Grid>

									{
										nonRunningVehicle &&
										<Grid item xs={6}>
											<FormCheckbox<IVehicleStatusForm>
												name={`subRreasonCode`}
												label={t('raCheckOutIn.vehicleStatus.label.vehicleTheft')}
												color={'primary'}
												control={vehicleStatusForm.control}
												disabled
											/>
										</Grid>
									}
								</>
							}
						</Grid>
					</Grid>

					<Grid item container xs={12} columnSpacing={2}>
						<Grid item xs={6} md={3}>
							<FormTextField<IVehicleStatusForm>
								name='kms'
								control={vehicleStatusForm.control}
								label={t("raCheckOutIn.vehicleStatus.label.kmOnStart")}
								disabled
							/>
						</Grid>

						<Grid item xs={6}  md={3} sx={{ display: "flex", alignItems: "flex-start" }}>
							<FormTextField<IVehicleStatusForm>
								name='kmsOnCheck'
								control={vehicleStatusForm.control}
								label={t("raCheckOutIn.vehicleStatus.label.kmOnCheck")}
								sx={{
									mr: theme.spacing(1),
									...(
										showEngineRunning
										? { marginBottom: "-0.75rem" } // Fix alignment with helperText
										: {}
									)
								}}
								disabled={nonRunningVehicle}
								helperText={showEngineRunning ? t("raCheckOutIn.vehicleStatus.label.engineRunning") : undefined}
							/>

							{
								(['subIn'].includes(checkOutInMode) && nonRunningVehicle) &&
								<IconButton component="label" disabled
											sx={{alignSelf: "flex-end", color: theme.palette.primary.main}}>
									<UploadRounded />
								</IconButton>
							}

							{
								(['out', 'in', 'subIn'].includes(checkOutInMode) && !kmsOnCheckPhoto && !nonRunningVehicle) &&
								<IconButton component="label"
											sx={{alignSelf: !!errors.kmsOnCheck || !!errors.tankLevelOnCheck || !!errors.batteryLevelOnCheck? "center" : "flex-end",
													color:  !!errors.kmsOnCheckPhoto ? theme.palette.error.main : theme.palette.primary.main}}>
									<input hidden accept="image/*"
										   type="file"
										   onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, 'kmsOnCheckPhoto')}
									/>
									<UploadRounded />
								</IconButton>
							}

							{
								(['out', 'in', 'subIn'].includes(checkOutInMode) && !!kmsOnCheckPhoto && !nonRunningVehicle) &&
								<Box sx={{display: "flex", justifyContent: "start",
											alignSelf: !!errors.kmsOnCheck || !!errors.tankLevelOnCheck || !!errors.batteryLevelOnCheck ? "center" : "flex-end"}}>
									<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleViewAttachFile(e, 'kmsOnCheckPhoto')}>
										<VisibilityRounded />
									</IconButton>
									<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleDeleteAttachFile(e, 'kmsOnCheckPhoto')}>
										<DeleteRounded />
									</IconButton>
								</Box>
							}
						</Grid>

						{
							TERMIC_HYBRID_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
							<>
								<Grid item xs={6} md={3}>
									<FormTextField<IVehicleStatusForm>
										name='currentTankFilling'
										control={vehicleStatusForm.control}
										label={t("raCheckOutIn.vehicleStatus.label.fuelLevelOnStart")}
										disabled
									/>
								</Grid>
								<Grid item xs={6} md={3}  sx={{ display: "flex", alignItems: "flex-start" }}>
									<FormAutocomplete<IVehicleStatusForm>
										name="tankLevelOnCheck"
										control={vehicleStatusForm.control}
										label={t("raCheckOutIn.vehicleStatus.label.fuelLevelOnCheck")}
										fullWidth
										options={tankLevelOptions}
										isOptionEqualToValue={(option, value) => option?.code === value?.code}
										getOptionLabel={(opt) => opt?.description}
										sx={{mr: theme.spacing(1)}}
										disabled={nonRunningVehicle}
									/>

									{
										(['subIn'].includes(checkOutInMode) && nonRunningVehicle) &&
										<IconButton component="label" disabled
													sx={{alignSelf: "flex-end", color: theme.palette.primary.main}}>
											<UploadRounded />
										</IconButton>
									}

									{
										(['out', 'in', 'subIn'].includes(checkOutInMode) && !tankLevelOnCheckPhoto && !nonRunningVehicle) &&
										<IconButton  component="label"
													 sx={{alignSelf: !!errors.kmsOnCheck || !!errors.tankLevelOnCheck ? "center" : "flex-end",
														 color:  !!errors.tankLevelOnCheckPhoto ? theme.palette.error.main : theme.palette.primary.main}}>
											<input hidden accept="image/*"
												   type="file"
												   onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, 'tankLevelOnCheckPhoto')}
											/>
											<UploadRounded />
										</IconButton>
									}

									{
										(['out', 'in', 'subIn'].includes(checkOutInMode) && !!tankLevelOnCheckPhoto && !nonRunningVehicle) &&
										<Box sx={{display: "flex", justifyContent: "start",
											alignSelf: !!errors.kmsOnCheck || !!errors.tankLevelOnCheck ? "center" : "flex-end"}}>
											<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleViewAttachFile(e, 'tankLevelOnCheckPhoto')}>
												<VisibilityRounded />
											</IconButton>
											<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleDeleteAttachFile(e, 'tankLevelOnCheckPhoto')}>
												<DeleteRounded />
											</IconButton>
										</Box>
									}
								</Grid>
							</>
						}

						{
							ELECTRIC_ENGINE_POWER_CODE.includes(enginePowerCode as string) &&
							<>
								<Grid item xs={6} md={3}>
									<FormTextField<IVehicleStatusForm>
										name='currentBatteryFilling'
										control={vehicleStatusForm.control}
										label={t("raCheckOutIn.vehicleStatus.label.batteryLevelOnStart")}
										disabled
									/>
								</Grid>

								<Grid item xs={6} md={3}  sx={{ display: "flex", alignItems: "flex-start" }}>
									<FormAutocomplete<IVehicleStatusForm>
										name="batteryLevelOnCheck"
										control={vehicleStatusForm.control}
										label={t("raCheckOutIn.vehicleStatus.label.batteryLevelOnCheck")}
										fullWidth
										options={batteryLevelOptions}
										isOptionEqualToValue={(option, value) => option?.code === value?.code}
										getOptionLabel={(opt) => opt?.description}
										disabled={nonRunningVehicle}
									/>

									{
										(['subIn'].includes(checkOutInMode) && nonRunningVehicle) &&
										<IconButton component="label" disabled
													sx={{alignSelf: "flex-end", color: theme.palette.primary.main}}>
											<UploadRounded />
										</IconButton>
									}

									{
										(['out', 'in', 'subIn'].includes(checkOutInMode) && !batteryLevelOnCheckPhoto && !nonRunningVehicle) &&
										<IconButton component="label"
													sx={{alignSelf: !!errors.kmsOnCheck || !!errors.batteryLevelOnCheck ? "center" : "flex-end",
														color:  !!errors.batteryLevelOnCheckPhoto ? theme.palette.error.main : theme.palette.primary.main}}>
											<input hidden accept="image/*"
												   type="file"
												   onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, 'batteryLevelOnCheckPhoto')}
											/>
											<UploadRounded />
										</IconButton>
									}

									{
										(['out', 'in', 'subIn'].includes(checkOutInMode) && !!batteryLevelOnCheckPhoto && !nonRunningVehicle) &&
										<Box sx={{display: "flex", justifyContent: "start",
													alignSelf: !!errors.kmsOnCheck || !!errors.batteryLevelOnCheck ? "center" : "flex-end"}}>
											<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleViewAttachFile(e, 'batteryLevelOnCheckPhoto')}>
												<VisibilityRounded />
											</IconButton>
											<IconButton color="primary" onClick={(e: React.SyntheticEvent) => handleDeleteAttachFile(e, 'batteryLevelOnCheckPhoto')}>
												<DeleteRounded/>
											</IconButton>
										</Box>
									}
								</Grid>
								<Grid item xs={6} md={3} />
							</>
						}
					</Grid>
				</Grid>
			</FormProvider>

			<CheckStepper />

			<Box sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
				<Button color={"accent"} onClick={vehicleStatusForm.handleSubmit(handleContinue)} disabled={props.hasUnconfirmedCustomerReportsValue}>
					{t('raCheckOutIn.vehicleStatus.button.continue')}
				</Button>
			</Box>

			<Preview
				open={openPreviewAttach}
				attach={filePreview}
				onClose={handleClosePreview}
			/>

			<CustomLoader loading={updateVehicleStatusMutation.isLoading} />
		</Paper>
	);
}

export default VehicleStatus;